import { useCallback, useEffect, useMemo, useState } from "react"
import { useAppDispatch } from "../../../../app/hooks"
import { loadProcessTeamMembers, ProjectProcessTeamMember } from "../ProjectProcessApiClient"
import { AppId } from "../../../../app/appTypes"
import { unwrapResult } from "@reduxjs/toolkit"
import produce from "immer";
import { ErrorContainer } from "../../../../app/ErrorContainer"
import { LoadingContainer } from "../../../../app/LoadingContainer"
import { Box, Button, ButtonGroup, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from "@mui/material"
import { Localized } from "@fluent/react"
import { If } from "../../../../app/If"
import DeleteIcon from '@mui/icons-material/Delete';
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import BadgeAvatar from "../../../mockups/BadgeAvatar"
import ProcessTeamMemberRoleChip from "./ProcessTeamMemberRoleChip"
import { ProcessTeamMemberDialog, ProcessTeamMemberDialogProps } from "../dialogs/ProcessTeamMemberDialog"
import { DeleteProcessTeamMemberDialog, DeleteProcessTeamMemberDialogProps } from "../dialogs/DeleteProcessTeamMemberDialog"
import { InviteProcessTeamMemberDialog, InviteProcessTeamMemberDialogProps } from "../dialogs/InviteProcessTeamMemberDialog"


type DialogState
    = { tag: 'none' }
    | { tag: 'addEditTeamMember', props: ProcessTeamMemberDialogProps }
    | { tag: 'inviteTeamMember', props: InviteProcessTeamMemberDialogProps }
    | { tag: 'confirmTeamMemberDelete', props: DeleteProcessTeamMemberDialogProps }

export interface ProcessTeamMembersPanelProps {
    processId: AppId
}

export const ProcessTeamMembersPanel = ({ processId }: ProcessTeamMembersPanelProps) => {
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [teamMembers, setTeamMembers] = useState<ProjectProcessTeamMember[]>();
    const teamMembersSelection = useMemo(() => new Set(teamMembers?.map(u => u.user)), [teamMembers])

    const [dialog, setDialog] = useState<DialogState>({ tag: 'none' })
    const save = true

    useEffect(() => {
        setIsLoading(true)
        dispatch(loadProcessTeamMembers(processId))
            .then(unwrapResult)
            .then(setTeamMembers)
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [])

    const handleDeleteTeamMemberClick = (entity: ProjectProcessTeamMember) => {
        if (processId) {
            setDialog({
                tag: 'confirmTeamMemberDelete',
                props: {
                    teamMember: entity,
                    onSave: (teamMemberId: AppId) => {
                        setTeamMembers(prev =>
                            produce(prev, draft => {
                                if (!draft) return;
                                const idx = draft.findIndex(u => u.id === teamMemberId);
                                if (idx !== -1) draft.splice(idx, 1);
                            })
                        );
                        setDialog({ tag: 'none' });
                    },
                    onCancel: () => setDialog({ tag: 'none' })
                }
            })
        }
    }

    const handleAddTeamMemberClick = useCallback(() => {
        if (processId) {
            setDialog({
                tag: 'addEditTeamMember',
                props: {
                    processId: processId,
                    mode: "create",
                    excludedUsers: teamMembersSelection,
                    defaultUserName: "",
                    onSave: (tm) => {
                        setDialog({ tag: 'none' })
                        setTeamMembers(
                            produce(draft => {
                                if (draft !== undefined) {
                                    draft.push(tm)
                                }
                            })
                        )
                        setDialog({ tag: 'none' })
                    },
                    onCancel: () => setDialog({ tag: 'none' })
                }
            })
        }
    }, [processId])

    const handleInviteClick = useCallback(() => {
        if (processId) {
            setDialog({
                tag: 'inviteTeamMember',
                props: {
                    processId: processId,
                    onSave: (tm) => {
                        setDialog({ tag: 'none' })
                        setTeamMembers(
                            produce(draft => {
                                if (draft !== undefined) {
                                    draft.push(tm)
                                }
                            })
                        )
                        setDialog({ tag: 'none' })
                    },
                    onCancel: () => setDialog({ tag: 'none' })
                }
            })
        }
    }, [processId])



    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return <>
        <TableContainer component={Paper} sx={{
            maxHeight: 'calc(100vh - 400px)',
        }}>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={7} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <GroupsIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                                <Localized id="document-team">
                                    <span>_team</span>
                                </Localized>
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell width={48}>#</TableCell>
                        <TableCell sx={{ minWidth: 260 }}>
                            <Localized id="user"><span>Użytkownik</span></Localized>
                        </TableCell>
                        <TableCell sx={{ width: 180 }}>
                            <Localized id="user-role"><span>Rola</span></Localized>
                        </TableCell>
                        <TableCell sx={{ width: 220, display: { xs: 'none', sm: 'table-cell' } }}>
                            <Localized id="company"><span>Firma</span></Localized>
                        </TableCell>
                        <TableCell sx={{ width: 80 }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {teamMembers?.map((tm, index) => (
                        <TeamMemberRow
                            entity={tm}
                            index={index}
                            onDeleteClick={handleDeleteTeamMemberClick}
                        />
                    ))}
                </TableBody>
            </Table>

        </TableContainer >
        <Box sx={{
            padding: 1,
        }}>
            <If condition={save !== undefined}>
                <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />}
                    onClick={handleAddTeamMemberClick}>
                    <Localized id="add">
                        <span>_Dodaj</span>
                    </Localized>
                </Button>
                <Button sx={{ marginLeft: "5px", marginTop: "1px" }}
                    size="small" variant="outlined" startIcon={<PublicRoundedIcon />}
                    onClick={handleInviteClick} >
                    <Localized id="invite">
                        <span>_Zaproś</span>
                    </Localized>
                </Button>

            </If>
        </Box>
        <DialogDispatcher dialog={dialog} />
    </>
}

const DialogDispatcher = ({ dialog }: { dialog: DialogState }) => {
    switch (dialog.tag) {
        case 'none':
            return null

        case 'addEditTeamMember':
            return <ProcessTeamMemberDialog {...dialog.props} />

        case 'inviteTeamMember':
            return <InviteProcessTeamMemberDialog {...dialog.props} />

        case 'confirmTeamMemberDelete':
            return <DeleteProcessTeamMemberDialog {...dialog.props} />
    }
}

interface TeamMemberRowProps {
    entity: ProjectProcessTeamMember
    index: number
    onDeleteClick: (entity: ProjectProcessTeamMember) => void
}
const TeamMemberRow = ({ entity, index, onDeleteClick }: TeamMemberRowProps) => {
    const { user, firstName, surname, position, companyName } = entity

    const handleDeleteClick = useCallback(() => {
        onDeleteClick(entity)
    }, [entity, onDeleteClick])

    return <TableRow>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
            <Box display="flex" alignItems="center">

                <BadgeAvatar firstName={firstName} surname={surname} userId={entity.id} />
                <Box ml={1}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                            {firstName} {surname}
                        </Typography>
                        {Boolean(entity.isExternal) && <Chip
                            icon={<PublicRoundedIcon />}
                            label={<Localized id="project-external"><span>Zewnętrzny</span></Localized>}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{
                                height: 20,
                                '& .MuiChip-label': {
                                    px: 0.75,
                                    fontSize: "0.68rem",
                                },
                                '& .MuiChip-icon': {
                                    fontSize: 13,
                                    ml: 0.625,
                                    mr: -0.25,
                                },
                            }}
                        />}
                    </Box>

                    {position && (
                        <Typography variant="caption" color="text.secondary">
                            {position}
                        </Typography>
                    )}

                    <Box display="flex" alignItems="center" gap={0.5} mt={0.2}>
                        <Typography variant="caption" fontWeight={500} color="text.secondary">
                            {companyName || '-'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

        </TableCell>
        <TableCell><ProcessTeamMemberRoleChip role={entity.role} /></TableCell>
        <TableCell>{companyName}</TableCell>

        <TableCell>
            <ButtonGroup>
                <Tooltip title={<Localized id="delete">Usuń</Localized>}>
                    <IconButton size="small" onClick={handleDeleteClick}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </ButtonGroup>
        </TableCell>
    </TableRow>
}

